<script setup lang="ts">
import { computed, PropType, ref } from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import { useI18n } from "vue-i18n";
import { useApiClient } from "@/api/client";
import { useDashboardStore } from "@/modules/dashboards/store";
import {
	filterHasInvalidDataSource,
	filterSupportsQuickFilter,
} from "@/modules/explorer/components/FilterBar/filterUtils";
import useDialog from "@/ui/composables/useDialog";
import { useFilterDialog } from "@/modules/explorer/components/FilterDialog/useFilterDialog";
import DimensionTypes = App.Enums.DimensionTypes;
const dialog = useDialog();

const { openFilterDialog } = useFilterDialog();
const { t } = useI18n();

const props = defineProps({
	activeFilter: {
		type: Object as PropType<App.Data.Explorer.Filter.ActiveFilterDTO>,
		require: true,
	},
	index: {
		type: Number,
		required: true,
	},
	surface: {
		type: String as PropType<"explorer" | "dashboard">,
		required: true,
	},
});

const isExplorer = computed(() => {
	return props.surface === "explorer";
});

const hasInvalidDataSource = computed(() =>
	isExplorer.value
		? filterHasInvalidDataSource(props.activeFilter, true)
		: false
);

const canUseQuickFilter = computed(() =>
	filterSupportsQuickFilter(props.activeFilter)
);

const canUseInGraph = computed(() => {
	const allowed: DimensionTypes[] = [
		"nominal",
		"bucketed",
		"hierarchy",
		"datetime",
	];

	return allowed.includes(props.activeFilter.dimension.type as DimensionTypes);
});

const matchingPin = computed(() =>
	useExplorerStore().explorerState.activePins.find(
		(pin) =>
			pin.dimension.name_clean === props.activeFilter.dimension.name_clean
	)
);

// Role-sourced pins can't be unpinned from here — they're managed on the role itself.
const isPinnedByRole = computed(() => matchingPin.value?.source === "role");

const togglePinDimension = async () => {
	if (matchingPin.value) {
		const choice = await dialog.confirm({
			title: "Unpin chip",
			message: "Are you sure you want to unpin this chip?",
		});
		if (!choice) return;
	}

	const result = await useApiClient("users").toggleCurrentUserFavoriteDimension(
		props.activeFilter.dimension
	);
	if (result.isOk()) {
		useExplorerStore().ADD_PIN({
			dimension: props.activeFilter.dimension,
			isApplied: true,
			valuesAreIncluded: true,
			source: "user",
		});
	}
};

const activeFiltersTooltip = computed(
	() => "Values: " + props.activeFilter.values.map((af) => af.label).join(", ")
);

const activeSpecification = computed((): string | null => {
	if (props.activeFilter?.specification) {
		return (
			t(`properties.${props.activeFilter?.dimension.name_clean}.title`) +
			" definition '" +
			props.activeFilter?.specification.name +
			"'"
		);
	}
	return null;
});

const isPinned = computed(() => !!matchingPin.value);

const removeFilter = () => {
	if (isExplorer.value) {
		useExplorerStore().REMOVE_FILTER_BY_INDEX(props.index);
		return;
	}
	isMenuOpen.value = false;
	useDashboardStore().removeFilterByIndex(props.index);
};

const toggleInclusion = () => {
	if (isExplorer.value) {
		useExplorerStore().TOGGLE_FILTER_INCLUSION(props.index);
		return;
	}
	useDashboardStore().toggleFilterInclusion(props.index);
};

const toggleUsage = () => {
	if (isExplorer.value) {
		useExplorerStore().TOGGLE_FILTER_USAGE(props.index);
		return;
	}
	useDashboardStore().toggleFilterUsage(props.index);
};

const toggleQuickFilter = () => {
	if (isExplorer.value) {
		useExplorerStore().TOGGLE_QUICK_FILTER(props.index);
		return;
	}
	useDashboardStore().toggleQuickFilter(props.index);
};

const editFilter = () => {
	if (isExplorer.value) {
		useExplorerStore().explorerState.selectedFilter = props.activeFilter;
	} else {
		useDashboardStore().dashboardState.selectedFilter = props.activeFilter;
	}

	// HACK: For now in the explorer period filters have no editor to open
	if (isExplorer.value) {
		if (
			useExplorerStore().explorerState.selectedFilter.dimension.type !==
			"datetime"
		) {
			openFilterDialog();
		}
	} else {
		openFilterDialog();
	}
};

const toggleDimensionOnAxis = (axis) => {
	if (
		axis === "horizontal_dimension" &&
		useExplorerStore().document[axis]?.name_clean ===
			props.activeFilter.dimension.name_clean
	) {
		return; // you can't unset the horizontal
	}

	useExplorerStore().document[axis] =
		useExplorerStore().document[axis]?.name_clean ===
		props.activeFilter.dimension.name_clean
			? null
			: props.activeFilter.dimension;
};

const chipColor = computed(() => {
	if (!props.activeFilter.isApplied) return "grey";
	if (hasInvalidDataSource.value) return "warning";
	return props.activeFilter.valuesAreIncluded ? "success" : "error";
});

const isMenuOpen = ref(false);
</script>

<template>
	<v-chip
		:color="chipColor"
		class="pr-0"
		size="small"
		@click="editFilter"
		style="flex-shrink: 0; max-width: none"
		:key="index + 'chip'"
		data-testid="filter-chip"
	>
		<div class="w-100 h-100 d-flex">
			<div class="d-flex align-center ga-1 h-100 pr-3">
				<v-tooltip>
					<template #activator="{ props }">
						<v-icon
							v-if="hasInvalidDataSource"
							v-bind="props"
							class="ml-n1"
							size="15"
						>
							mdi-alert-outline
						</v-icon>
					</template>
					This filter is not available with the current verticals, it will be
					re-enabled when available
				</v-tooltip>
				<!-- flex on both this and the parent element is required to prevent large content from wrapping -->
				<div class="d-flex align-center ga-1 text-caption">
					<v-tooltip
						activator="parent"
						location="top"
						v-if="activeFilter.values.length > 1"
						>{{ activeFiltersTooltip }}
					</v-tooltip>
					<div style="text-overflow: ellipsis">
						{{ t(`properties.${activeFilter.dimension.name_clean}.title`) }}
					</div>
					<template v-if="activeFilter.dimension.type === 'datetime'">
						-
						<i
							>[
							{{
								t(
									`property_families.${activeFilter.dimension.family_name_clean}.title`
								)
							}}
							]</i
						>
					</template>
				</div>
				<v-tooltip activator="parent" location="top" v-if="activeSpecification">
					{{ activeSpecification }}
				</v-tooltip>
			</div>
		</div>

		<template v-slot:append="">
			<div class="d-flex">
				<v-btn
					v-if="
						activeFilter.dimension.type !== 'hierarchy' &&
						!(
							activeFilter.dimension.type === 'datetime' &&
							activeFilter.dimension.granularity === 'day'
						)
					"
					@click.stop="toggleInclusion"
					:icon="
						activeFilter.valuesAreIncluded
							? 'mdi-vector-intersection'
							: 'mdi-vector-difference'
					"
					v-tooltip="
						(activeFilter.valuesAreIncluded ? 'Exclude' : 'Include') + ' filter'
					"
					rounded="0"
					class="ma-0"
					size="32"
					variant="tonal"
					:color="chipColor"
				>
				</v-btn>
				<v-btn
					:icon="
						activeFilter.isApplied ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
					"
					v-tooltip="
						(activeFilter.isApplied ? 'Disable' : 'Enable') + ' filter'
					"
					rounded="0"
					@click.stop="toggleUsage"
					class="ma-0"
					size="32"
					variant="tonal"
					:color="chipColor"
				>
				</v-btn>
				<v-btn
					v-if="canUseQuickFilter"
					v-tooltip="
						(activeFilter.isQuickFilter ? 'Disable' : 'Enable') +
						' quick filter menu'
					"
					@click.stop="toggleQuickFilter"
					rounded="0"
					class="ma-0"
					size="32"
					variant="tonal"
					:active="activeFilter.isQuickFilter"
					:color="chipColor"
					data-testid="quick-filter-button"
				>
					<i class="fa-light fa-list-tree fa-xl"></i>
				</v-btn>
				<v-btn
					v-if="!isExplorer"
					@click.stop="removeFilter"
					icon="mdi-trash-can-outline"
					rounded="0"
					class="ma-0"
					size="32"
					variant="tonal"
					:color="chipColor"
				>
				</v-btn>
				<v-menu
					v-else
					v-model="isMenuOpen"
					transition="scale-transition"
					:close-on-content-click="false"
					rounded="0"
				>
					<template v-slot:activator="{ props }">
						<v-btn
							v-bind="props"
							icon="fa: fa-regular fa-ellipsis-vertical fa-lg"
							variant="tonal"
							size="32"
							rounded="0"
							:color="chipColor"
						/>
					</template>

					<v-card class="pa-2 ga-2" min-width="275">
						<template v-if="canUseInGraph">
							<span style="font-size: 0.875rem">Use dimension on:</span>
							<div class="d-flex mt-2 ga-2">
								<v-btn
									:disabled="
										useExplorerStore().document.horizontal_dimension
											?.name_clean === props.activeFilter.dimension.name_clean
									"
									variant="tonal"
									size="small"
									class="flex-grow-1"
									@click="toggleDimensionOnAxis('horizontal_dimension')"
								>
									Horizontal
								</v-btn>
								<v-btn
									:disabled="
										useExplorerStore().document.offset_dimension?.name_clean ===
										props.activeFilter.dimension.name_clean
									"
									variant="tonal"
									size="small"
									class="flex-grow-1"
									@click="toggleDimensionOnAxis('offset_dimension')"
								>
									Offset
								</v-btn>
								<v-btn
									:disabled="
										useExplorerStore().document.legend_dimension?.name_clean ===
										props.activeFilter.dimension.name_clean
									"
									variant="tonal"
									size="small"
									class="flex-grow-1"
									@click="toggleDimensionOnAxis('legend_dimension')"
								>
									Legend
								</v-btn>
							</div>
							<v-divider class="my-2" />
						</template>
						<div class="d-flex ga-2">
							<v-btn
								variant="tonal"
								color="error"
								size="small"
								class="flex-grow-1"
								@click="removeFilter"
							>
								<v-icon size="16"> mdi-trash-can-outline </v-icon>
								<div class="ml-1">Remove Filter</div>
							</v-btn>
							<v-btn
								v-if="!isPinned || !isPinnedByRole"
								min-width="115"
								variant="tonal"
								color="inactive"
								size="small"
								class="flex-grow-1"
								@click="togglePinDimension"
							>
								<v-icon size="16">
									mdi-pin{{ isPinned ? "-off-" : "-" }}outline
								</v-icon>
								<div class="ml-1">{{ isPinned ? "Unpin" : "Pin" }} Filter</div>
							</v-btn>
						</div>
					</v-card>
				</v-menu>
			</div>
		</template>
	</v-chip>
</template>

<style scoped></style>
